import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import {
  Check,
  Loader2,
  Package,
  ChefHat,
  Bike,
  Home,
  XCircle,
  MapPin,
  Utensils,
  Navigation,
} from "lucide-react";
import DeliveryMap from "@/components/DeliveryMap";

// =========================================================
// DELIVERY ORDER STEPS
// =========================================================

const DELIVERY_STEPS = [
  { key: "placed", label: "Order Placed", icon: Check },
  { key: "accepted", label: "Accepted", icon: Check },
  { key: "preparing", label: "Preparing", icon: ChefHat },
  { key: "ready", label: "Packed", icon: Package },
  {
    key: "out_for_delivery",
    label: "Out for Delivery",
    icon: Bike,
  },
  {
    key: "delivered",
    label: "Delivered",
    icon: Home,
  },
];

// =========================================================
// DINE-IN ORDER STEPS
// =========================================================

const DINE_IN_STEPS = [
  { key: "placed", label: "Order Placed", icon: Check },
  { key: "accepted", label: "Accepted", icon: Check },
  { key: "preparing", label: "Preparing", icon: ChefHat },
  { key: "ready", label: "Ready", icon: Package },
  {
    key: "completed",
    label: "Completed",
    icon: Utensils,
  },
];

export default function OrderTracking() {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [lookup, setLookup] = useState("");
  const [err, setErr] = useState("");

  // =======================================================
  // FETCH ORDER
  // =======================================================

  useEffect(() => {
    if (!orderId) return;

    let mounted = true;

    const fetchOrder = async () => {
      try {
        const response = await api.get(
          `/orders/${encodeURIComponent(orderId)}`
        );

        if (!mounted) return;

        setOrder(response.data);
        setErr("");
      } catch (error) {
        if (!mounted) return;

        setOrder(null);

        setErr(
          error?.response?.data?.detail ||
            "Order not found"
        );
      }
    };

    fetchOrder();

    const interval = setInterval(
      fetchOrder,
      5000
    );

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [orderId]);

  // =======================================================
  // TRACKING SEARCH PAGE
  // =======================================================

  if (!orderId) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24">
        <h1 className="font-heading text-5xl mb-6">
          Track Your Order
        </h1>

        <p className="text-[#E6F4F2] mb-6">
          Enter your order code:
        </p>

        <div className="flex gap-3">
          <input
            data-testid="track-input"
            value={lookup}
            onChange={(e) =>
              setLookup(
                e.target.value.toUpperCase()
              )
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                const code = lookup.trim();

                if (code) {
                  navigate(`/track/${code}`);
                }
              }
            }}
            placeholder="ICP-XXXXXX"
            className="flex-1 rounded-full border border-[rgba(184,225,74,0.35)] bg-white px-6 py-3 text-[#1F5F5C] focus:ring-2 focus:ring-[#B8E14A] outline-none"
          />

          <button
            data-testid="track-submit"
            onClick={() => {
              const code = lookup.trim();

              if (code) {
                navigate(`/track/${code}`);
              }
            }}
            className="btn-primary"
          >
            Track
          </button>
        </div>
      </div>
    );
  }

  // =======================================================
  // ERROR
  // =======================================================

  if (err) {
    return (
      <div className="max-w-2xl mx-auto py-24 px-6 text-center">
        <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />

        <h2 className="font-heading text-3xl mb-2">
          Order not found
        </h2>

        <p className="text-[#E6F4F2] mb-6">
          {err}
        </p>

        <button
          onClick={() => navigate("/track")}
          className="btn-primary"
        >
          Track Another Order
        </button>
      </div>
    );
  }

  // =======================================================
  // LOADING
  // =======================================================

  if (!order) {
    return (
      <div className="max-w-2xl mx-auto py-24 text-center">
        <Loader2 className="w-8 h-8 mx-auto animate-spin text-[#B8E14A]" />

        <p className="text-[#B7D8D5] mt-4">
          Loading order...
        </p>
      </div>
    );
  }

  // =======================================================
  // ORDER TYPE
  // =======================================================

  const isDineIn =
    String(order.order_type || "").toUpperCase() ===
    "DINE_IN";

  const isDelivery = !isDineIn;

  const STEPS = isDineIn
    ? DINE_IN_STEPS
    : DELIVERY_STEPS;

  // =======================================================
  // CURRENT STATUS
  // =======================================================

  const currentIdx = STEPS.findIndex(
    (step) => step.key === order.status
  );

  const rejected =
    order.status === "rejected";

  // =======================================================
  // DELIVERY DESTINATION
  //
  // IMPORTANT:
  // Backend stores customer-selected destination at:
  //
  // order.delivery.latitude
  // order.delivery.longitude
  // =======================================================

  const deliveryLatitude =
    order?.delivery?.latitude;

  const deliveryLongitude =
    order?.delivery?.longitude;

  const hasDeliveryDestination =
    isDelivery &&
    Number.isFinite(
      Number(deliveryLatitude)
    ) &&
    Number.isFinite(
      Number(deliveryLongitude)
    );

  // =======================================================
  // DELIVERY PARTNER LIVE LOCATION
  //
  // Backend stores live delivery partner location at:
  //
  // order.delivery_lat
  // order.delivery_lng
  // =======================================================

  const liveLatitude =
    order?.delivery_lat;

  const liveLongitude =
    order?.delivery_lng;

  const hasLiveDeliveryLocation =
    isDelivery &&
    Number.isFinite(
      Number(liveLatitude)
    ) &&
    Number.isFinite(
      Number(liveLongitude)
    );

  // =======================================================
  // CUSTOMER MAP LINK
  // =======================================================

  const openMap = () => {
    if (order?.delivery?.map_link) {
      window.open(
        order.delivery.map_link,
        "_blank",
        "noopener,noreferrer"
      );

      return;
    }

    if (hasDeliveryDestination) {
      const url =
        `https://www.google.com/maps/search/?api=1&query=` +
        `${deliveryLatitude},${deliveryLongitude}`;

      window.open(
        url,
        "_blank",
        "noopener,noreferrer"
      );

      return;
    }
  };

  // =======================================================
  // RENDER
  // =======================================================

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-12 py-8 md:py-12">

      {/* ===================================================
          HEADER
      =================================================== */}

      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-[#B8E14A] mb-2">
          Order
        </div>

        <h1 className="font-heading text-5xl">
          {order.order_code}
        </h1>

        <div className="text-sm text-[#B7D8D5] mt-1">
          Placed on{" "}
          {order.created_at
            ? new Date(
                order.created_at
              ).toLocaleString()
            : "—"}
        </div>
      </div>

      {/* ===================================================
          ORDER TYPE
      =================================================== */}

      <div className="glass rounded-2xl p-4 mb-4 flex items-center gap-3">

        {isDineIn ? (
          <>
            <Utensils className="w-5 h-5 text-[#B8E14A]" />

            <div>
              <div className="text-xs uppercase tracking-widest text-[#B7D8D5]">
                Order Type
              </div>

              <div className="font-semibold">
                Dine-In
              </div>
            </div>
          </>
        ) : (
          <>
            <Bike className="w-5 h-5 text-[#B8E14A]" />

            <div>
              <div className="text-xs uppercase tracking-widest text-[#B7D8D5]">
                Order Type
              </div>

              <div className="font-semibold">
                Delivery
              </div>
            </div>
          </>
        )}

      </div>

      {/* ===================================================
          DELIVERY DESTINATION
      =================================================== */}

      {isDelivery && (
        <div className="glass rounded-3xl p-5 mb-8">

          <div className="flex items-center justify-between gap-4 mb-4">

            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#B8E14A]" />

              <div>
                <div className="text-xs uppercase tracking-widest text-[#B7D8D5]">
                  Delivery Address
                </div>

                <div className="font-semibold">
                  Customer-selected location
                </div>
              </div>
            </div>

            {hasDeliveryDestination && (
              <button
                onClick={openMap}
                className="text-xs px-4 py-2 rounded-full bg-[#B8E14A] text-[#1F5F5C] font-semibold"
              >
                <Navigation className="inline w-3 h-3 mr-1" />
                Open Map
              </button>
            )}

          </div>

          <div className="text-sm text-[#E6F4F2]">
            {order.delivery?.address ||
              "Address not provided"}
          </div>

          {order.delivery?.landmark && (
            <div className="text-sm text-[#B7D8D5] mt-1">
              Landmark:{" "}
              {order.delivery.landmark}
            </div>
          )}

          {order.delivery?.pincode && (
            <div className="text-sm text-[#B7D8D5] mt-1">
              Pincode:{" "}
              {order.delivery.pincode}
            </div>
          )}

          {hasDeliveryDestination && (
            <div className="text-xs text-[#B7D8D5] mt-3">
              📍 Location selected from map
            </div>
          )}

          {order.shop_name && (
            <div className="mt-4 pt-4 border-t border-[rgba(184,225,74,0.25)]">

              <div className="text-xs uppercase tracking-widest text-[#B7D8D5]">
                Assigned Shop
              </div>

              <div className="font-semibold mt-1">
                {order.shop_name}
              </div>

              {order.shop_distance_km != null && (
                <div className="text-xs text-[#B7D8D5] mt-1">
                  Approximately{" "}
                  {Number(
                    order.shop_distance_km
                  ).toFixed(2)}{" "}
                  km from delivery location
                </div>
              )}

            </div>
          )}

        </div>
      )}

      {/* ===================================================
          DINE-IN DETAILS
      =================================================== */}

      {isDineIn && (
        <div className="glass rounded-2xl p-5 mb-8">

          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-5 h-5 text-[#B8E14A]" />

            <div className="text-xs uppercase tracking-widest text-[#B7D8D5]">
              Dine-In Details
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">

            <div>
              <div className="text-xs text-[#B7D8D5]">
                Shop
              </div>

              <div className="font-semibold mt-1">
                {order.shop_name ||
                  "Selected Shop"}
              </div>

              {order.shop_address && (
                <div className="text-xs text-[#B7D8D5] mt-1">
                  {order.shop_address}
                </div>
              )}
            </div>

            <div>
              <div className="text-xs text-[#B7D8D5]">
                Table
              </div>

              <div className="font-heading text-2xl text-[#B8E14A] mt-1">
                {order.table_no || "—"}
              </div>
            </div>

          </div>
        </div>
      )}

      {/* ===================================================
          PAYMENT
      =================================================== */}

      <div className="glass rounded-2xl p-4 mb-8 flex items-center justify-between gap-4">

        <div>
          <div className="text-xs uppercase tracking-widest text-[#B7D8D5]">
            Payment
          </div>

          <div className="font-medium">
            {order.payment_method || "—"} ·{" "}

            <span
              className={
                order.payment_status === "verified"
                  ? "text-green-700"
                  : order.payment_status === "rejected"
                  ? "text-red-600"
                  : "text-[#B8E14A]"
              }
            >
              {order.payment_status ||
                "pending"}
            </span>
          </div>
        </div>

        <div className="text-right">
          <div className="text-xs uppercase tracking-widest text-[#B7D8D5]">
            Total
          </div>

          <div className="font-heading text-2xl text-[#B8E14A]">
            ₹{order.total}
          </div>
        </div>

      </div>

      {/* ===================================================
          REJECTED
      =================================================== */}

      {rejected ? (

        <div className="glass rounded-3xl p-8 text-center">

          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />

          <div className="font-heading text-3xl">
            Order Rejected
          </div>

          <div className="text-sm text-[#B7D8D5] mt-1">
            Please contact support.
          </div>

        </div>

      ) : (

        /* =================================================
           TIMELINE
        ================================================= */

        <div className="glass rounded-3xl p-8">

          <div className="text-xs uppercase tracking-widest text-[#B7D8D5] mb-6">
            Progress
          </div>

          <div className="relative pl-6">

            <div className="absolute left-[15px] top-2 bottom-2 w-0.5 bg-[rgba(184,225,74,0.35)]" />

            {STEPS.map(
              (step, index) => {

                const done =
                  currentIdx >= 0 &&
                  index <= currentIdx;

                const active =
                  index === currentIdx;

                const Icon =
                  step.icon;

                return (
                  <motion.div
                    key={step.key}
                    initial={{
                      opacity: 0,
                      x: -10,
                    }}
                    animate={{
                      opacity: 1,
                      x: 0,
                    }}
                    transition={{
                      delay:
                        index * 0.08,
                    }}
                    className="relative pb-6"
                    data-testid={`step-${step.key}`}
                  >

                    <div
                      className={`absolute -left-6 w-8 h-8 rounded-full flex items-center justify-center ${
                        done
                          ? "bg-[#B8E14A] text-white"
                          : "bg-white text-[#1F5F5C] border border-[rgba(184,225,74,0.35)]"
                      } ${
                        active
                          ? "ring-4 ring-[#B8E14A]/30"
                          : ""
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                    </div>

                    <div className="ml-4">

                      <div
                        className={`font-medium ${
                          done
                            ? "text-white"
                            : "text-[#B7D8D5]"
                        }`}
                      >
                        {step.label}
                      </div>

                      {active && (
                        <div className="text-xs text-[#B8E14A] mt-1">
                          {order.status ===
                          "delivered"
                            ? "Order delivered successfully"
                            : order.status ===
                              "completed"
                            ? "Order completed"
                            : "In progress..."}
                        </div>
                      )}

                    </div>

                  </motion.div>
                );
              }
            )}

          </div>

        </div>
      )}

      {/* ===================================================
          DELIVERY MAP
      =================================================== */}

      {isDelivery &&
        order.status ===
          "out_for_delivery" && (
          <div className="glass rounded-3xl p-5 mt-8">

            <div className="text-xs uppercase tracking-widest text-[#B8E14A] mb-3">
              Live Delivery Tracking
            </div>

            {hasLiveDeliveryLocation ? (
              <>
                <DeliveryMap
                  lat={Number(
                    liveLatitude
                  )}
                  lng={Number(
                    liveLongitude
                  )}
                  destinationLat={
                    hasDeliveryDestination
                      ? Number(
                          deliveryLatitude
                        )
                      : null
                  }
                  destinationLng={
                    hasDeliveryDestination
                      ? Number(
                          deliveryLongitude
                        )
                      : null
                  }
                />

                <div className="text-xs text-[#B7D8D5] mt-3">
                  🚴 Delivery partner location
                  updates automatically.
                </div>
              </>
            ) : hasDeliveryDestination ? (
              <>
                <DeliveryMap
                  lat={Number(
                    deliveryLatitude
                  )}
                  lng={Number(
                    deliveryLongitude
                  )}
                  destinationLat={Number(
                    deliveryLatitude
                  )}
                  destinationLng={Number(
                    deliveryLongitude
                  )}
                />

                <div className="text-xs text-[#B7D8D5] mt-3">
                  📍 Your delivery destination
                  is selected. Live delivery
                  partner location will appear
                  when GPS tracking starts.
                </div>
              </>
            ) : (
              <div className="rounded-2xl bg-white/10 p-6 text-center">
                <MapPin className="w-8 h-8 mx-auto text-[#B8E14A] mb-2" />

                <div className="font-semibold">
                  Delivery location unavailable
                </div>

                <div className="text-xs text-[#B7D8D5] mt-1">
                  No GPS coordinates were saved
                  with this order.
                </div>
              </div>
            )}

          </div>
        )}

      {/* ===================================================
          ITEMS
      =================================================== */}

      <div className="glass rounded-3xl p-6 mt-8">

        <div className="text-xs uppercase tracking-widest text-[#B7D8D5] mb-4">
          Items
        </div>

        <div className="space-y-3">

          {(order.items || []).map(
            (item, index) => (

              <div
                key={index}
                className="flex justify-between gap-4 text-sm"
              >

                <div>

                  <div className="font-medium">
                    {item.name} ×{" "}
                    {item.quantity}
                  </div>

                  {item.options?.size && (
                    <div className="text-xs text-[#B7D8D5]">
                      {item.options.size}
                      {item.options.cone
                        ? ` · ${item.options.cone}`
                        : ""}
                    </div>
                  )}

                  {Array.isArray(
                    item.options?.flavours
                  ) &&
                    item.options.flavours
                      .length > 0 && (
                      <div className="text-xs text-[#B7D8D5]">
                        {item.options.flavours.join(
                          ", "
                        )}
                      </div>
                    )}

                </div>

                <div className="text-[#B8E14A] font-medium whitespace-nowrap">
                  ₹
                  {Number(
                    item.price || 0
                  ) *
                    Number(
                      item.quantity || 0
                    )}
                </div>

              </div>
            )
          )}

        </div>

      </div>

      {/* ===================================================
          CUSTOMER DETAILS
      =================================================== */}

      <div className="glass rounded-3xl p-6 mt-4">

        <div className="text-xs uppercase tracking-widest text-[#B7D8D5] mb-2">
          {isDineIn
            ? "Customer"
            : "Delivery Customer"}
        </div>

        <div className="text-sm">
          {order.delivery?.name ||
            "—"}

          {order.delivery?.phone && (
            <>
              {" · "}
              {order.delivery.phone}
            </>
          )}
        </div>

        {isDelivery && (
          <>
            <div className="text-sm text-[#E6F4F2] mt-2">
              {order.delivery?.address ||
                "Address not provided"}

              {order.delivery?.landmark
                ? `, ${order.delivery.landmark}`
                : ""}

              {order.delivery?.pincode
                ? ` — ${order.delivery.pincode}`
                : ""}
            </div>

            {order.delivery
              ?.delivery_time && (
              <div className="text-xs text-[#B7D8D5] mt-1">
                Delivery time:{" "}
                {
                  order.delivery
                    .delivery_time
                }
              </div>
            )}
          </>
        )}

        {isDineIn && (
          <div className="text-xs text-[#B7D8D5] mt-1">
            Dine-in at{" "}
            {order.shop_name ||
              "selected shop"}
            {" · "}
            {order.table_no || "—"}
          </div>
        )}

      </div>

    </div>
  );
}